/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 照片状态管理 (RTK Slice)
 * @LastEditors: refactor
 * @LastEditTime: 2026-03-25
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import dayjs from 'dayjs'

// 筛选条件类型
interface PhotoFilter {
  searchKeywords: string[]
  mediaType: string | number | null
  sourceType: string | number | null
  times: [any, any] // dayjs 对象
  departmentUsers: any[]
  departments: any[]
  watermarks: any[]
  locations: any[]
  useSynonym: boolean
  useSegment: boolean
  useSuggestedSearch: boolean
  suggestedSearchWords: any[]
  existWatermark: number
  [key: string]: any
}

// 扩展信息类型
interface ExpandInfo {
  expandSynonym: {
    total: number
    words: string[]
  }
  expandSegment: Array<{ total: number; word: string }>
}

// 照片类型
interface Photo {
  seq: number
  largeURL?: string
  smallURL?: string
  videoURL?: string
  photoRectification?: any
  [key: string]: any
}

// 客户端下载任务类型
interface ClientDownloadTask {
  id: string
  progress?: number
  downloadedCount?: number
  totalCount?: number
  status?: string
  error?: string
  [key: string]: any
}

// State 类型
interface PhotoState {
  filter: PhotoFilter
  queryFilter: PhotoFilter | null
  photos: Photo[]
  expandInfo: ExpandInfo
  isAscSort: boolean
  allWatermarksNum: number
  canFavorite: boolean
  checkedPhotos: Record<number, Photo>
  unCheckedPhotos: Record<number, Photo>
  checkAll: boolean
  photosNum: number
  lastPageCond: string
  exportTasks: any[]
  exportListDrawerVisible: boolean
  exportTaskTimer: ReturnType<typeof setTimeout> | null
  currentViewPhoto: Photo | null
  exportDownloadPhotoOnceParams: any | null
  aiVisualSearchAB: boolean
  oldPhotosCount: number
  searchTrackingId: string
  isGroupPhotoViewLimit: boolean
  loading?: boolean
  clientDownloadDrawerVisible: boolean
  clientDownloadTasks: ClientDownloadTask[]
  clientDownloadProgress: any | null
  // 额外字段（一些组件可能访问）
  dates?: any
  lastSeq?: any
  originalPhotos?: any[]
}

// 创建初始 filter
const createInitialFilter = (): PhotoFilter => ({
  searchKeywords: [],
  mediaType: null,
  sourceType: null,
  times: [dayjs().subtract(6, 'months').startOf('day'), dayjs().endOf('day')],
  departmentUsers: [],
  departments: [],
  watermarks: [],
  locations: [],
  useSynonym: true,
  useSegment: true,
  useSuggestedSearch: true,
  suggestedSearchWords: [],
  existWatermark: 0
})

const initialState: PhotoState = {
  filter: createInitialFilter(),
  queryFilter: null,
  photos: [],
  expandInfo: {
    expandSynonym: {
      total: 0,
      words: []
    },
    expandSegment: []
  },
  isAscSort: false,
  allWatermarksNum: 0,
  canFavorite: false,
  checkedPhotos: {},
  unCheckedPhotos: {},
  checkAll: false,
  photosNum: 0,
  lastPageCond: '',
  exportTasks: [],
  exportListDrawerVisible: false,
  exportTaskTimer: null,
  currentViewPhoto: null,
  exportDownloadPhotoOnceParams: null,
  aiVisualSearchAB: false,
  oldPhotosCount: 0,
  searchTrackingId: '',
  isGroupPhotoViewLimit: false,
  clientDownloadDrawerVisible: false,
  clientDownloadTasks: [],
  clientDownloadProgress: null
}

const photoSlice = createSlice({
  name: 'photo',
  initialState,
  reducers: {
    // 设置筛选条件（对应 SET_PHOTOS_FILTER）
    setPhotosFilter(state, action: PayloadAction<PhotoFilter>) {
      state.filter = action.payload
    },

    // 设置照片列表（对应 SET_PHOTOS）
    setPhotos(state, action: PayloadAction<Photo[]>) {
      state.photos = action.payload
    },

    // 设置是否可以收藏（对应 SET_CAN_FAVORITE）
    setCanFavorite(state, action: PayloadAction<boolean>) {
      state.canFavorite = action.payload
    },

    // 追加照片（对应 APPEND_PHOTOS）
    appendPhotos(state, action: PayloadAction<Photo[]>) {
      state.photos = [...state.photos, ...action.payload]
    },

    // 重置筛选条件（对应 RESET_FILTER）
    resetFilter(state) {
      state.filter = createInitialFilter()
      state.queryFilter = createInitialFilter()
    },

    // 设置查询筛选条件（对应 SET_QUERY_FILTER）
    setQueryFilter(state, action: PayloadAction<PhotoFilter | null>) {
      state.queryFilter = action.payload
    },

    // 设置照片数量（对应 SET_PHOTOS_NUM）
    setPhotosNum(state, action: PayloadAction<number>) {
      state.photosNum = action.payload
    },

    // 设置扩展信息（对应 SET_PHOTOS_EXPAND_INFO）
    setPhotosExpandInfo(state, action: PayloadAction<ExpandInfo>) {
      state.expandInfo = action.payload
    },

    // 设置所有水印数量（对应 SET_WATERMARKS_NUM）
    setWatermarksNum(state, action: PayloadAction<number>) {
      state.allWatermarksNum = action.payload
    },

    // 设置最后一页条件（对应 SET_LAST_PAGE_COND）
    setLastPageCond(state, action: PayloadAction<string>) {
      state.lastPageCond = action.payload
    },

    // 设置全选状态（对应 SET_CHECK_ALL）
    setCheckAll(state, action: PayloadAction<boolean>) {
      state.checkAll = action.payload
    },

    // 添加选中的照片（对应 PUSH_CHECKED_PHOTO）
    pushCheckedPhoto(state, action: PayloadAction<Photo>) {
      const photo = action.payload
      state.checkedPhotos[photo.seq] = photo
    },

    // 添加取消选中的照片（对应 PUSH_UNCHECKED_PHOTO）
    pushUnCheckedPhoto(state, action: PayloadAction<Photo>) {
      const photo = action.payload
      state.unCheckedPhotos[photo.seq] = photo
    },

    // 移除选中的照片（对应 REMOVE_CHECKED_PHOTO）
    removeCheckedPhoto(state, action: PayloadAction<Photo>) {
      const photo = action.payload
      delete state.checkedPhotos[photo.seq]
    },

    // 移除取消选中的照片（对应 REMOVE_UNCHECKED_PHOTO）
    removeUnCheckedPhoto(state, action: PayloadAction<Photo>) {
      const photo = action.payload
      delete state.unCheckedPhotos[photo.seq]
    },

    // 清除所有选中的照片（对应 CLEAR_CHECKED_PHOTO）
    clearCheckedPhoto(state) {
      state.checkedPhotos = {}
    },

    // 清除所有取消选中的照片（对应 CLEAR_UNCHECKED_PHOTO）
    clearUnCheckedPhoto(state) {
      state.unCheckedPhotos = {}
    },

    // 设置导出任务（对应 SET_EXPORT_TASKS）
    setExportTasks(state, action: PayloadAction<any[]>) {
      state.exportTasks = action.payload
    },

    // 设置导出列表抽屉可见性（对应 SET_EXPORT_LIST_DRAWER_VISIBLE）
    setExportListDrawerVisible(state, action: PayloadAction<boolean>) {
      state.exportListDrawerVisible = action.payload
    },

    // 设置导出任务定时器（对应 SET_EXPORT_TASK_TIMER）
    setExportTaskTimer(
      state,
      action: PayloadAction<ReturnType<typeof setTimeout> | null>
    ) {
      state.exportTaskTimer = action.payload
    },

    // 设置当前查看的照片（对应 SET_CURRENT_VIEW_PHOTO）
    setCurrentViewPhoto(state, action: PayloadAction<Photo | null>) {
      state.currentViewPhoto = action.payload
    },

    // 设置导出下载照片一次参数（对应 SET_EXPORT_DOWNLOAD_PHOTO_ONCE_PARAMS）
    setExportDownloadPhotoOnceParams(state, action: PayloadAction<any | null>) {
      state.exportDownloadPhotoOnceParams = action.payload
    },

    // 设置 AI 视觉搜索 AB（对应 SET_AI_VISUAL_SEARCH_AB）
    setAiVisualSearchAB(state, action: PayloadAction<boolean>) {
      state.aiVisualSearchAB = action.payload
    },

    // 清除照片状态（对应 CLEAR_PHOTO）
    clearPhoto() {
      return {
        ...initialState,
        // 重新创建 filter 以获取新的时间戳
        filter: createInitialFilter()
      }
    },

    // 设置90天前照片数量（对应 SET_OLD_PHOTOS_COUNT）
    setOldPhotosCount(state, action: PayloadAction<number>) {
      state.oldPhotosCount = action.payload
    },

    // 设置搜索追踪 ID（对应 SET_SEARCH_TRACKING_ID）
    setSearchTrackingId(state, action: PayloadAction<string>) {
      state.searchTrackingId = action.payload
    },

    // 设置排序方向（对应 SET_IS_ASC_SORT）
    setIsAscSort(state, action: PayloadAction<boolean>) {
      state.isAscSort = action.payload
    },

    // 更新照片整改字段（对应 UPDATE_PHOTO_RECTIFICATION）
    updatePhotoRectification(
      state,
      action: PayloadAction<{ photoUrl: string; photoRectification: any }>
    ) {
      const { photoUrl, photoRectification } = action.payload
      if (!photoUrl || !state.photos.length) return

      const cleanPhotoUrl = photoUrl.split('?')[0]

      for (let i = 0; i < state.photos.length; i++) {
        const photo = state.photos[i]
        const cleanLargeURL = photo.largeURL?.split('?')[0] || ''
        const cleanSmallURL = photo.smallURL?.split('?')[0] || ''
        const cleanVideoURL = photo.videoURL?.split('?')[0] || ''

        if (
          cleanLargeURL === cleanPhotoUrl ||
          cleanSmallURL === cleanPhotoUrl ||
          cleanVideoURL === cleanPhotoUrl
        ) {
          state.photos[i] = {
            ...photo,
            photoRectification
          }
          break
        }
      }
    },

    // 设置大团队照片查看限制（对应 SET_IS_GROUP_PHOTO_VIEW_LIMIT）
    setIsGroupPhotoViewLimit(state, action: PayloadAction<boolean>) {
      state.isGroupPhotoViewLimit = action.payload
    },

    // 设置客户端下载抽屉可见性（对应 SET_CLIENT_DOWNLOAD_DRAWER_VISIBLE）
    setClientDownloadDrawerVisible(state, action: PayloadAction<boolean>) {
      state.clientDownloadDrawerVisible = action.payload
    },

    // 设置客户端下载任务（对应 SET_CLIENT_DOWNLOAD_TASKS）
    setClientDownloadTasks(state, action: PayloadAction<ClientDownloadTask[]>) {
      state.clientDownloadTasks = action.payload
    },

    // 设置客户端下载进度（对应 SET_CLIENT_DOWNLOAD_PROGRESS）
    setClientDownloadProgress(
      state,
      action: PayloadAction<{
        taskId: string
        progress?: number
        downloadedCount?: number
        totalCount?: number
        status?: string
        error?: string
      }>
    ) {
      const { taskId, progress, downloadedCount, totalCount, status, error } =
        action.payload
      const taskIndex = state.clientDownloadTasks.findIndex(
        (task) => task.id === taskId
      )
      if (taskIndex !== -1) {
        const task = state.clientDownloadTasks[taskIndex]
        state.clientDownloadTasks[taskIndex] = {
          ...task,
          progress: progress ?? task.progress,
          downloadedCount: downloadedCount ?? task.downloadedCount,
          totalCount: totalCount ?? task.totalCount,
          status: status ?? task.status,
          error: error ?? task.error
        }
      }
    }
  },
  extraReducers: (builder) => {
    // 兼容旧的 dispatch({ type: actionTypes.XXX, data: ... }) 调用
    builder
      .addMatcher(
        (action) => action.type === 'SET_CHECK_ALL',
        (state, action: any) => {
          state.checkAll = action.data
        }
      )
      .addMatcher(
        (action) => action.type === 'PUSH_CHECKED_PHOTO',
        (state, action: any) => {
          state.checkedPhotos[action.data.seq] = action.data
        }
      )
      .addMatcher(
        (action) => action.type === 'REMOVE_CHECKED_PHOTO',
        (state, action: any) => {
          delete state.checkedPhotos[action.data.seq]
        }
      )
      .addMatcher(
        (action) => action.type === 'CLEAR_CHECKED_PHOTO',
        (state) => {
          state.checkedPhotos = {}
        }
      )
      .addMatcher(
        (action) => action.type === 'PUSH_UNCHECKED_PHOTO',
        (state, action: any) => {
          state.unCheckedPhotos[action.data.seq] = action.data
        }
      )
      .addMatcher(
        (action) => action.type === 'REMOVE_UNCHECKED_PHOTO',
        (state, action: any) => {
          delete state.unCheckedPhotos[action.data.seq]
        }
      )
      .addMatcher(
        (action) => action.type === 'CLEAR_UNCHECKED_PHOTO',
        (state) => {
          state.unCheckedPhotos = {}
        }
      )
      .addMatcher(
        (action) => action.type === 'SET_PHOTOS',
        (state, action: any) => {
          state.photos = action.data || []
        }
      )
      .addMatcher(
        (action) => action.type === 'SET_PHOTOS_NUM',
        (state, action: any) => {
          state.photosNum = action.data || 0
        }
      )
      .addMatcher(
        (action) => action.type === 'SET_LOADING',
        (state, action: any) => {
          state.loading = action.data
        }
      )
      .addMatcher(
        (action) => action.type === 'SET_PHOTOS_EXPAND_INFO',
        (state, action: any) => {
          state.expandInfo = action.data
        }
      )
      .addMatcher(
        (action) => action.type === 'SET_WATERMARKS_NUM',
        (state, action: any) => {
          state.allWatermarksNum = action.data
        }
      )
      .addMatcher(
        (action) => action.type === 'SET_CURRENT_VIEW_PHOTO',
        (state, action: any) => {
          state.currentViewPhoto = action.data
        }
      )
      .addMatcher(
        (action) => action.type === 'SET_EXPORT_DOWNLOAD_PHOTO_ONCE_PARAMS',
        (state, action: any) => {
          state.exportDownloadPhotoOnceParams = action.data
        }
      )
      .addMatcher(
        (action) => action.type === 'SET_SEARCH_TRACKING_ID',
        (state, action: any) => {
          state.searchTrackingId = action.data
        }
      )
  }
})

export const {
  setPhotosFilter,
  setPhotos,
  setCanFavorite,
  appendPhotos,
  resetFilter,
  setQueryFilter,
  setPhotosNum,
  setPhotosExpandInfo,
  setWatermarksNum,
  setLastPageCond,
  setCheckAll,
  pushCheckedPhoto,
  pushUnCheckedPhoto,
  removeCheckedPhoto,
  removeUnCheckedPhoto,
  clearCheckedPhoto,
  clearUnCheckedPhoto,
  setExportTasks,
  setExportListDrawerVisible,
  setExportTaskTimer,
  setCurrentViewPhoto,
  setExportDownloadPhotoOnceParams,
  setAiVisualSearchAB,
  clearPhoto,
  setOldPhotosCount,
  setSearchTrackingId,
  setIsAscSort,
  updatePhotoRectification,
  setIsGroupPhotoViewLimit,
  setClientDownloadDrawerVisible,
  setClientDownloadTasks,
  setClientDownloadProgress
} = photoSlice.actions

export default photoSlice.reducer

// 类型导出
export type { PhotoState, PhotoFilter, Photo, ExpandInfo, ClientDownloadTask }
